import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useParams } from "react-router-dom";
import {
  ChevronRight,
  Send,
  Sparkles,
  Lock,
  ArrowUpRight,
  Receipt,
} from "lucide-react";
import { useRole } from "../context/RoleContext";
import {
  getOwnerConsultation,
  postOwnerMessage,
  requestIteration,
} from "../lib/ownerPortal";
import { ConsultPill, relativeDate } from "../lib/ownerUi";
import {
  assetUrl,
  formatInt,
  formatPriceFull,
  formatTypology,
} from "../lib/format";
import BackBar from "../components/BackBar";
import WorkflowStrip from "../components/WorkflowStrip";

/* ──────────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────────── */
export default function OwnerConsultation() {
  const { consultId } = useParams();
  const { user } = useRole();

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [iterOpen, setIterOpen] = useState(false);
  const threadEndRef = useRef(null);

  const refresh = async () => {
    try {
      const d = await getOwnerConsultation(consultId, user.id);
      setData(d);
    } catch (e) {
      setErr(e);
    }
  };

  useEffect(() => {
    if (!user?.id || !consultId) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, consultId]);

  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ block: "end" });
    }
  }, [data]);

  if (err) {
    return (
      <p style={{ color: "#A23B2E" }} data-testid="owner-consult-error">
        Could not load this consultation.
      </p>
    );
  }
  if (!data) {
    return (
      <div
        style={{ color: "#6C6B85", fontStyle: "italic" }}
        data-testid="owner-consult-loading"
      >
        Loading consultation…
      </div>
    );
  }

  const terminal = data.status === "Activated" || data.status === "Declined";
  const messages = data.thread?.messages || [];
  const variants = data.design?.iterations || [];
  const lastMsg = messages[messages.length - 1];

  /* Who's holding the ball right now (mirrors stageBlocker on projects). */
  const waitingOn = (() => {
    if (terminal) {
      return data.status === "Activated"
        ? { who: "Activated", tone: "done", note: data.project_id ? "Live in Projects" : "Consultation closed" }
        : { who: "Declined", tone: "done", note: "Consultation closed" };
    }
    if (!lastMsg) {
      return { who: "You", tone: "owner", note: "Open the conversation with a hello" };
    }
    if (lastMsg.sender_id === user.id) {
      return {
        who: "Architect",
        tone: "architect",
        note: `Sent ${relativeDate(lastMsg.sent_at)}`,
      };
    }
    return {
      who: "Your reply",
      tone: "owner",
      note: `They wrote ${relativeDate(lastMsg.sent_at)}`,
    };
  })();

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await postOwnerMessage(consultId, user.id, body.trim());
      setBody("");
      await refresh();
    } finally {
      setSending(false);
    }
  };

  const onIterationSubmitted = async () => {
    setIterOpen(false);
    await refresh();
  };

  return (
    <section className="animate-fade-in-up" data-testid="owner-consult-page">
      {/* ─── Unified back + breadcrumbs ─── */}
      <BackBar
        parent={{ to: "/owner/messages", label: "Messages" }}
        trail={[
          { label: "Owner", to: "/owner" },
          { label: "Messages", to: "/owner/messages" },
          { label: data.architect?.firm || "Consultation" },
        ]}
      />

      {/* ─── Header ─── */}
      <Header data={data} waitingOn={waitingOn} />

      {/* ─── Activated banner ─── */}
      {data.status === "Activated" && data.project_id && (
        <div
          className="mt-5"
          style={{
            border: "1px solid #4F6149",
            borderLeft: "3px solid #4F6149",
            background: "rgba(79,97,73,0.06)",
            padding: "14px 18px",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 16,
          }}
          data-testid="owner-consult-activated-banner"
        >
          <div>
            <div
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 16,
                color: "#4F6149",
              }}
            >
              This consultation is activated.
            </div>
            <p style={{ color: "#272636", fontSize: 13, marginTop: 2 }}>
              A project record has been created — continue all coordination there.
            </p>
          </div>
          <Link
            to={`/owner/projects/${data.project_id}`}
            className="wn-btn wn-btn-primary inline-flex items-center"
            style={{ gap: 6 }}
          >
            Open project <ArrowUpRight size={14} />
          </Link>
        </div>
      )}

      {/* ─── Body: conversation column + brief sidebar ─── */}
      <div className="mt-8 grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-10">
        <div>
          <ConversationColumn
            messages={messages}
            userId={user.id}
            threadEndRef={threadEndRef}
          />

          {/* Composer */}
          {!terminal ? (
            <Composer
              body={body}
              setBody={setBody}
              sending={sending}
              onSend={send}
              onRequestIteration={() => setIterOpen(true)}
            />
          ) : (
            <div
              className="mt-6"
              style={{
                borderTop: "1px solid #D9D8E4",
                paddingTop: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#6C6B85",
                fontStyle: "italic",
                fontFamily: '"Newsreader", Georgia, serif',
                fontSize: 14,
              }}
              data-testid="owner-composer-locked"
            >
              <Lock size={14} /> This conversation is closed for new messages.
            </div>
          )}
        </div>

        <Brief data={data} />
      </div>

      {iterOpen && (
        <IterationModal
          consultId={consultId}
          userId={user.id}
          variants={variants}
          onClose={() => setIterOpen(false)}
          onSubmitted={onIterationSubmitted}
        />
      )}
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
   Header — eyebrow + Fraunces firm + italic dek + status pill +
   "waiting on" strip directly below the H1.
   ────────────────────────────────────────────────────────────── */
function Header({ data, waitingOn }) {
  const dotColor =
    waitingOn.tone === "owner"
      ? "#B14E2F"
      : waitingOn.tone === "architect"
        ? "#1B1F4B"
        : "#9AA0C0";
  return (
    <header>
      <div
        className="flex items-start justify-between flex-wrap"
        style={{ gap: 16 }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 28,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              Consultation
            </span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(28px, 4vw, 38px)",
              fontWeight: 500,
              color: "#1B1F4B",
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              margin: 0,
            }}
            data-testid="owner-consult-firm"
          >
            {data.architect?.firm || "—"}
          </h1>
          <div
            className="mt-2"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 16,
              lineHeight: 1.5,
              maxWidth: 720,
            }}
          >
            {data.parcel?.address || "—"} · {data.design?.name || "—"}
          </div>
        </div>
        <ConsultPill status={data.status} testId="owner-consult-status" />
      </div>

      {/* Workflow strip — civic stepper for the consultation lifecycle. */}
      <WorkflowStrip
        domain="consultation"
        current={data.status}
        testId="owner-consult-workflow-strip"
      />

      {/* Waiting-on strip */}
      <div
        className="mt-5 flex items-center flex-wrap"
        style={{
          gap: 12,
          borderTop: "1px solid #D9D8E4",
          borderBottom: "1px solid #D9D8E4",
          padding: "10px 0",
        }}
        data-testid="owner-consult-waiting"
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: dotColor,
          }}
        />
        <span
          className="wn-label"
          style={{
            color: dotColor,
            letterSpacing: "0.10em",
          }}
        >
          Waiting on {waitingOn.who}
        </span>
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
          }}
        >
          · {waitingOn.note}
        </span>
      </div>
    </header>
  );
}

/* ──────────────────────────────────────────────────────────────
   ConversationColumn — editorial day-grouped feed. Hairline
   separators between days, day-stamps as Newsreader italic, no
   wn-card chrome.
   ────────────────────────────────────────────────────────────── */
function ConversationColumn({ messages, userId, threadEndRef }) {
  const days = useMemo(() => groupByDay(messages), [messages]);

  if (messages.length === 0) {
    return (
      <div
        className="mt-2"
        data-testid="owner-thread-empty"
        style={{
          padding: "40px 0",
          borderTop: "1px solid #D9D8E4",
          borderBottom: "1px solid #D9D8E4",
          color: "#6C6B85",
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          fontSize: 15,
        }}
      >
        No messages yet — open the conversation with a hello below.
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid="owner-thread">
      {days.map((d, di) => (
        <div key={d.key} style={{ marginTop: di === 0 ? 0 : 24 }}>
          {/* Day-stamp header */}
          <div
            className="flex items-center"
            style={{
              gap: 12,
              paddingBottom: 8,
              borderBottom: "1px solid #ECEBF1",
              marginBottom: 14,
            }}
          >
            <span
              className="wn-label tabular-nums"
              style={{
                color: "#272636",
                letterSpacing: "0.08em",
              }}
            >
              {d.label}
            </span>
            <span
              style={{
                flex: 1,
                height: 1,
                background: "transparent",
              }}
            />
          </div>
          <div className="space-y-4">
            {d.items.map((m, i) => (
              <Bubble
                key={m.id || `${m.sent_at}-${i}`}
                m={m}
                userId={userId}
              />
            ))}
          </div>
        </div>
      ))}
      <div ref={threadEndRef} />
    </div>
  );
}

function Bubble({ m, userId }) {
  const isMine = m.sender_id === userId;
  const isIter = m.kind === "iteration_request";
  return (
    <div
      className="flex"
      style={{ justifyContent: isMine ? "flex-end" : "flex-start" }}
      data-testid={isMine ? "msg-mine" : "msg-theirs"}
    >
      <div
        style={{
          maxWidth: "78%",
          minWidth: 120,
          padding: "12px 14px",
          borderRadius: 6,
          background: isMine ? "#1B1F4B" : "#FFFFFF",
          color: isMine ? "#FBFAFD" : "#272636",
          border: isMine ? "1px solid #1B1F4B" : "1px solid #D9D8E4",
          boxShadow: "none",
        }}
      >
        {isIter && (
          <div
            className="flex items-center"
            style={{
              gap: 6,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: isMine ? "#E89B83" : "#8F3C23",
              marginBottom: 6,
            }}
          >
            <Sparkles size={12} />
            Iteration request{m.variant ? ` · ${m.variant}` : ""}
          </div>
        )}
        <div
          style={{
            fontSize: 14,
            whiteSpace: "pre-wrap",
            lineHeight: 1.55,
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {m.body}
        </div>
        <div
          className="tabular-nums"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 11.5,
            color: isMine ? "rgba(251,250,253,0.65)" : "#6C6B85",
            marginTop: 6,
            textAlign: isMine ? "right" : "left",
          }}
        >
          {formatTime(m.sent_at)}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Composer — hairline-bordered footer, not a card. Tertiary
   "Request iteration" + primary "Send".
   ────────────────────────────────────────────────────────────── */
function Composer({ body, setBody, sending, onSend, onRequestIteration }) {
  return (
    <div
      className="mt-6"
      style={{
        borderTop: "1px solid #D9D8E4",
        paddingTop: 18,
      }}
      data-testid="owner-composer"
    >
      <div className="wn-label" style={{ color: "#6C6B85", marginBottom: 8 }}>
        Your reply
      </div>
      <textarea
        className="wn-input"
        style={{
          minHeight: 96,
          resize: "vertical",
          background: "#FBFAFD",
        }}
        placeholder="Write to the architect…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        data-testid="owner-composer-input"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && body.trim() && !sending) {
            e.preventDefault();
            onSend();
          }
        }}
      />
      <div
        className="mt-3 flex items-center justify-between flex-wrap"
        style={{ gap: 12 }}
      >
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12.5,
          }}
        >
          Press <span className="tabular-nums">⌘↵</span> to send.
        </div>
        <div className="flex items-center" style={{ gap: 10 }}>
          <button
            type="button"
            className="wn-btn wn-btn-tertiary inline-flex items-center"
            style={{ gap: 6 }}
            onClick={onRequestIteration}
            data-testid="owner-iteration-toggle"
          >
            <Sparkles size={14} /> Request iteration
          </button>
          <button
            type="button"
            className="wn-btn wn-btn-primary inline-flex items-center"
            style={{ gap: 6 }}
            onClick={onSend}
            disabled={!body.trim() || sending}
            data-testid="owner-composer-send"
          >
            <Send size={14} /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Brief — single hairline-bordered sidebar with three internal
   sections (Design / Parcel / Receipt) separated by hairlines.
   ────────────────────────────────────────────────────────────── */
function Brief({ data }) {
  return (
    <aside
      data-testid="owner-consult-side"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        background: "#FBFAFD",
        alignSelf: "start",
        position: "sticky",
        top: 24,
      }}
    >
      <div style={{ padding: "14px 18px", borderBottom: "1px solid #ECEBF1" }}>
        <div className="wn-label" style={{ color: "#8F3C23" }}>
          Brief
        </div>
        <div
          className="mt-1"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 12.5,
          }}
        >
          What you and the architect are looking at.
        </div>
      </div>

      {/* Design */}
      {data.design && (
        <div style={{ padding: 18 }}>
          <div
            style={{
              width: "100%",
              aspectRatio: "4 / 3",
              backgroundImage: `url(${assetUrl(data.design.exterior_photo)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: 4,
              border: "1px solid #ECEBF1",
            }}
            aria-hidden
          />
          <div
            className="wn-label mt-3"
            style={{ color: "#8F3C23" }}
          >
            {formatTypology(data.design.typology)}
          </div>
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: 18,
              color: "#1B1F4B",
              lineHeight: 1.2,
              marginTop: 2,
            }}
          >
            {data.design.name}
          </div>
          <dl
            className="mt-3 grid grid-cols-2 gap-y-1 gap-x-3 tabular-nums"
            style={{ fontSize: 12.5, color: "#272636" }}
          >
            <dt style={{ color: "#6C6B85" }}>Beds / Baths</dt>
            <dd>
              {data.design.beds} / {data.design.baths}
            </dd>
            <dt style={{ color: "#6C6B85" }}>Sq Ft</dt>
            <dd>{formatInt(data.design.sqft)}</dd>
            <dt style={{ color: "#6C6B85" }}>Plans</dt>
            <dd
              style={{
                color: "#8F3C23",
                fontWeight: 700,
              }}
            >
              {formatPriceFull(data.design.plan_purchase_price)}
            </dd>
          </dl>
        </div>
      )}

      {/* Parcel */}
      {data.parcel && (
        <div
          style={{
            padding: "14px 18px",
            borderTop: "1px solid #ECEBF1",
          }}
        >
          <div className="wn-label" style={{ color: "#6C6B85" }}>
            Parcel
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: 15,
              color: "#1B1F4B",
              lineHeight: 1.3,
            }}
          >
            {data.parcel.address}
          </div>
          <div
            className="tabular-nums mt-1"
            style={{
              color: "#6C6B85",
              fontSize: 12.5,
              fontFamily: '"Nunito Sans", sans-serif',
            }}
          >
            {data.parcel.width_ft}′ × {data.parcel.depth_ft}′ ·{" "}
            {data.parcel.zoning}
          </div>
          <div
            className="mt-2 inline-flex items-center"
            data-testid="owner-consult-fit"
            style={{
              gap: 6,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: data.fit?.fits ? "#4F6149" : "#8a651f",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: data.fit?.fits ? "#4F6149" : "#8a651f",
              }}
            />
            {data.fit?.fits ? "Fits this design" : "Review recommended"}
          </div>
        </div>
      )}

      {/* Receipt */}
      {data.payment_receipt && (
        <Link
          to={`/owner/payments/${encodeURIComponent(
            data.payment_receipt.transaction_ref || "",
          )}`}
          className="wn-brief-receipt block"
          data-testid="owner-consult-receipt-link"
          style={{
            display: "block",
            padding: "14px 18px",
            borderTop: "1px solid #ECEBF1",
            textDecoration: "none",
            color: "inherit",
            transition: "background-color 200ms ease",
          }}
        >
          <div className="flex items-center justify-between" style={{ gap: 8 }}>
            <div className="wn-label" style={{ color: "#6C6B85" }}>
              Consultation receipt
            </div>
            <span
              className="inline-flex items-center"
              style={{
                gap: 4,
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#8F3C23",
              }}
            >
              View
              <ArrowUpRight size={12} />
            </span>
          </div>
          <div
            className="tabular-nums mt-2"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              color: "#8F3C23",
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: "-0.005em",
            }}
          >
            ${data.payment_receipt.amount}
          </div>
          <div
            className="tabular-nums mt-1 flex items-center"
            style={{ color: "#272636", fontSize: 12, gap: 6 }}
          >
            <Receipt size={12} color="#6C6B85" />
            {data.payment_receipt.transaction_ref}
          </div>
          <div style={{ color: "#6C6B85", fontSize: 11.5, marginTop: 2 }}>
            Card ending {data.payment_receipt.last4}
            {data.payment_receipt.paid_at && (
              <>
                {" · "}Paid {relativeDate(data.payment_receipt.paid_at)}
              </>
            )}
          </div>
        </Link>
      )}
    </aside>
  );
}

/* ──────────────────────────────────────────────────────────────
   IterationModal — 720 px centred portal'd dialog, mirroring
   PurchaseModal's chrome (header / progress / step content).
   ────────────────────────────────────────────────────────────── */
function IterationModal({ consultId, userId, variants, onClose, onSubmitted }) {
  const [variant, setVariant] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState(1);

  const ready = note.trim().length >= 10;

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const onSubmit = async () => {
    if (!ready) return;
    setBusy(true);
    setErr(null);
    try {
      await requestIteration(consultId, userId, note.trim(), variant || undefined);
      setStep(2);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send iteration request.");
    } finally {
      setBusy(false);
    }
  };

  const onDone = async () => {
    await onSubmitted();
  };

  return ReactDOM.createPortal((
    <div
      role="dialog"
      aria-modal="true"
      data-testid="iteration-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(27,31,75,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 100%)",
          maxHeight: "92vh",
          overflow: "auto",
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 6,
          boxShadow: "0 28px 60px rgba(27,31,75,0.30)",
        }}
      >
        {/* Header / stepper */}
        <div style={{ padding: "22px 28px 18px", borderBottom: "1px solid #D9D8E4" }}>
          <div className="flex items-start justify-between gap-4">
            <div style={{ minWidth: 0 }}>
              <div className="wn-label" style={{ color: "#8F3C23" }}>
                <Sparkles
                  size={12}
                  style={{ display: "inline", marginRight: 6, marginBottom: 2 }}
                />
                Iteration request
              </div>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 500,
                  fontSize: 24,
                  color: "#1B1F4B",
                  marginTop: 4,
                  lineHeight: 1.15,
                  letterSpacing: "-0.005em",
                }}
              >
                {step === 1 ? "Ask for a design iteration." : "Request sent."}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="iteration-modal-close"
              aria-label="Close"
              style={{
                width: 32,
                height: 32,
                borderRadius: 4,
                border: "1px solid #D9D8E4",
                background: "transparent",
                color: "#6C6B85",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
          <div
            className="flex items-center"
            style={{ gap: 6, marginTop: 18 }}
          >
            {[1, 2].map((n) => (
              <span
                key={n}
                data-testid={`iteration-step-${n}`}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 999,
                  background: n <= step ? "#B14E2F" : "#ECEBF1",
                  transition: "background-color 200ms ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Step content */}
        <div style={{ padding: "20px 28px 22px" }}>
          {step === 1 && (
            <>
              <p
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#6C6B85",
                  fontSize: 14,
                  lineHeight: 1.55,
                  margin: 0,
                  maxWidth: 560,
                }}
              >
                Iteration fees, if any, are agreed directly in the consultation
                thread — no separate charge is applied in this PoC.
              </p>

              {variants.length > 0 && (
                <>
                  <label className="wn-label mt-5 block" htmlFor="iter-variant">
                    Reference variant (optional)
                  </label>
                  <select
                    id="iter-variant"
                    className="wn-input mt-1"
                    value={variant}
                    onChange={(e) => setVariant(e.target.value)}
                    data-testid="owner-iter-variant"
                  >
                    <option value="">— none —</option>
                    {variants.map((v, i) => (
                      <option
                        key={i}
                        value={v.label || v.name || String(i)}
                      >
                        {v.label || v.name || `Variant ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label className="wn-label mt-5 block" htmlFor="iter-note">
                What would you like changed?
              </label>
              <textarea
                id="iter-note"
                className="wn-input mt-1"
                style={{ minHeight: 130, resize: "vertical" }}
                placeholder="At least 10 characters — describe the change you want explored."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="owner-iter-note"
              />
              <div
                className="mt-2 tabular-nums"
                style={{
                  fontSize: 11.5,
                  color: note.trim().length < 10 ? "#8a651f" : "#4F6149",
                }}
              >
                {note.trim().length}/10 characters minimum
              </div>

              {err && (
                <div
                  className="mt-3"
                  style={{ color: "#A23B2E", fontSize: 13 }}
                  data-testid="iteration-modal-error"
                >
                  {err}
                </div>
              )}

              <div className="mt-6 flex items-center justify-end" style={{ gap: 10 }}>
                <button
                  type="button"
                  className="wn-btn wn-btn-tertiary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="wn-btn wn-btn-primary inline-flex items-center"
                  style={{ gap: 6 }}
                  disabled={!ready || busy}
                  onClick={onSubmit}
                  data-testid="owner-iter-submit"
                >
                  <Send size={14} />{" "}
                  {busy ? "Sending…" : "Send iteration request"}
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <div data-testid="iteration-modal-done">
              <div
                className="inline-flex items-center"
                style={{
                  gap: 8,
                  background: "rgba(79,97,73,0.10)",
                  color: "#4F6149",
                  padding: "8px 14px",
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: "#4F6149",
                  }}
                />
                Sent to the architect
              </div>
              <p
                className="mt-5"
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  color: "#272636",
                  fontSize: 15,
                  lineHeight: 1.55,
                  maxWidth: 560,
                }}
              >
                The architect has been notified. You'll see their response in
                this conversation — usually within a few business days.
              </p>
              <div className="mt-6 flex items-center justify-end" style={{ gap: 10 }}>
                <button
                  type="button"
                  className="wn-btn wn-btn-primary"
                  onClick={onDone}
                  data-testid="iteration-modal-done-btn"
                >
                  Back to conversation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

/* ──────────────────────────────────────────────────────────────
   Day-grouping helpers
   ────────────────────────────────────────────────────────────── */
function groupByDay(messages) {
  const groups = new Map();
  for (const m of messages) {
    const day = (m.sent_at || "").slice(0, 10) || "earlier";
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day).push(m);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => ({ key, label: dayLabel(key), items }));
}

function dayLabel(day) {
  if (!day || day === "earlier") return "Earlier";
  try {
    const d = new Date(day + "T12:00:00Z");
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return "Today";
    const yest = new Date(today);
    yest.setDate(today.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return day;
  }
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
